import {
  CdkDrag,
  CdkDragDrop,
  CdkDragPlaceholder,
  CdkDragPreview,
  CdkDropList,
  CdkDropListGroup,
} from "@angular/cdk/drag-drop";
import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  Pipe,
  PipeTransform,
  signal,
} from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { Audience, AudienceCategory, AudienceStatus, StrategyDspAudienceType } from "@front/m19-api-client";
import { AudienceId, AudiencesService, DspStrategyService, isAmcAudience } from "@front/m19-services";
import {
  IBadgeComponent,
  IButtonDirective,
  IIconComponent,
  IInputGroupDirective,
  IInputGroupInput,
  IPaginationConfig,
  IPaginatorComponent,
  ISelectMenuImports,
  ITooltipDirective,
  Option,
} from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { DspAudienceLineComponent } from "@m19-board/dsp/dsp-audiences-selector/dsp-audience-line/dsp-audience-line.component";
import { DspAudienceWarningComponent } from "@m19-board/dsp/dsp-pending-audience-warning/dsp-audience-warning.component";
import { startWith, switchMap } from "rxjs";

@Pipe({ name: "isInAudienceGroup", standalone: true })
export class IsInAudienceGroupPipe implements PipeTransform {
  transform(audienceId: string, array: Audience[]): boolean {
    return array.some((audience) => audience.audienceId === audienceId);
  }
}

@Pipe({ name: "isAudienceSelectable" })
export class AudienceSelectablePipe implements PipeTransform {
  readonly ALLOWED_STATUS = [AudienceStatus.Active, AudienceStatus.Processing, AudienceStatus.Pending];

  transform(audience: Audience): boolean {
    return !audience.status || this.ALLOWED_STATUS.includes(audience.status);
  }
}

@Component({
  selector: "app-dsp-audiences-selector",
  imports: [
    CommonModule,
    TranslocoDirective,
    DspAudienceLineComponent,
    IBadgeComponent,
    CdkDropListGroup,
    CdkDropList,
    CdkDrag,
    IButtonDirective,
    CdkDragPlaceholder,
    IsInAudienceGroupPipe,
    CdkDragPreview,
    AudienceSelectablePipe,
    DspAudienceWarningComponent,
    IInputGroupDirective,
    IInputGroupInput,
    FormsModule,
    IPaginatorComponent,
    ITooltipDirective,
    IIconComponent,
    ISelectMenuImports,
  ],
  templateUrl: "./dsp-audiences-selector.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DspAudiencesSelectorComponent {
  private readonly audienceService = inject(AudiencesService);

  readonly DEFAULT_PAGE_SIZE = 10;
  readonly MAX_AUDIENCE_GROUP_PER_STRATEGY = DspStrategyService.MAX_AUDIENCE_GROUP_PER_STRATEGY;
  readonly MAX_AUDIENCE_PER_GROUP = DspStrategyService.MAX_AUDIENCE_PER_GROUP;
  readonly StrategyDspAudienceType = StrategyDspAudienceType;

  readonly audienceType = input(StrategyDspAudienceType.WHITELIST);
  readonly style = input<"rounded" | "outlined">("rounded");
  selectedAudiences = model<AudienceId[][]>([[]]);

  readonly searchFilter = signal<string>("");
  readonly audienceCategoryOption: Option<AudienceCategory>[] = Object.values(AudienceCategory).map((value) => ({
    label: value,
    value: value,
  }));

  selectedCategoryOptions = signal<Option<AudienceCategory>[]>(this.audienceCategoryOption);

  allDspAudiences = toSignal(this.audienceService.allDspAudiences$);
  filteredDspAudiences = computed(() => {
    return (this.allDspAudiences() ?? []).filter((audience) => {
      const isValidCategory = this.selectedCategoryOptions().some((o) => o.value === audience.category);
      const searchFilter = this.searchFilter().toLowerCase();

      return !isAmcAudience(audience) && audience.audienceName.toLowerCase().includes(searchFilter) && isValidCategory;
    });
  });
  paginatedDspAudiences = computed<Audience[]>(() => {
    if (this.filteredDspAudiences().length <= this.DEFAULT_PAGE_SIZE) return this.filteredDspAudiences();

    const start = this.pagination().pageIndex * this.pagination().pageSize;
    const end = start + this.pagination().pageSize;

    return this.filteredDspAudiences().slice(start, end);
  });

  audienceBeingDragged = signal<string | undefined>(undefined);
  draggingHoverGroup = signal<number | undefined>(undefined);

  selectedAudiences$ = toObservable(this.selectedAudiences);

  readonly resolvedAudiences = toSignal<Audience[][]>(
    this.selectedAudiences$.pipe(
      switchMap((audiences) => this.audienceService.getAudiencesFromIds(audiences)),
      startWith([]),
    ),
    { requireSync: true },
  );

  readonly pagination = signal<IPaginationConfig>({
    pageIndex: 0,
    pageSize: this.DEFAULT_PAGE_SIZE,
    length: 0,
  });

  startDrag(audience: Audience) {
    this.audienceBeingDragged.set(audience.audienceId);
  }

  endDrag() {
    this.audienceBeingDragged.set(undefined);
  }

  drop(event: CdkDragDrop<Audience>, groupIndex: number) {
    this.draggingHoverGroup.set(undefined);
    const audience = event.item.data as Audience | undefined;
    if (!audience) return;

    this.addAudience(audience, groupIndex);
  }

  clickOnAudience(audience: Audience) {
    this.addAudience(audience, this.selectedAudiences().length - 1);
  }

  addAudience(audience: Audience, groupIndex: number) {
    this.selectedAudiences.update((audienceGroups) => {
      if (audienceGroups.length === 0) {
        return [[{ audienceId: audience.audienceId }]];
      }
      const audienceGroup = audienceGroups[groupIndex];
      if (audienceGroup.length >= this.MAX_AUDIENCE_PER_GROUP) {
        return audienceGroups;
      }

      if (!audienceGroup.some((a) => a.audienceId === audience.audienceId)) {
        audienceGroup.push({ audienceId: audience.audienceId });
      }
      return [...audienceGroups];
    });
  }

  deleteAudience(groupIndex: number, audienceIndex: number) {
    this.selectedAudiences.update((audiences) => {
      audiences[groupIndex].splice(audienceIndex, 1);
      if (audiences[groupIndex].length === 0) {
        audiences.splice(groupIndex, 1);
      }
      return audiences.length ? [...audiences] : [[]];
    });
  }

  addAudienceGroup() {
    this.selectedAudiences.update((audienceGroups) => {
      audienceGroups.push([]);
      return [...audienceGroups];
    });
  }

  removeGroup(groupIndex: number) {
    this.selectedAudiences.update((audienceGroups) => {
      return audienceGroups.filter((_, index) => index !== groupIndex);
    });
  }

  onSearch(search: string) {
    this.searchFilter.set(search);
    this.pagination.update((p) => ({ ...p, pageIndex: 0 }));
  }

  categoryFilterChange(selectedOptions: Option<AudienceCategory>[] | undefined) {
    this.selectedCategoryOptions.set(selectedOptions ?? []);
  }
}
